import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import EmployeeInformation from '../../../components/ui/EmployeeInformation';
import Dialog from '../../../components/ui/Dialog';
import NotYouDialog from '../../../components/ui/NotYouDialog';
import ErrorState from '../../../components/ui/ErrorState';
import NoDataState from '../../../components/ui/NoDataState';
import Tooltip from '../../../components/ui/Tooltip';
import DisabledTabTooltip from '../../../components/ui/DisabledTabTooltip';
import { useAuth } from '../../../hooks/useAuth';
import { useRBAC } from '../../../hooks/useRBAC';
import { teamReviewService, type TeamReviewOKRsData } from '../../../services/teamReviewService';
import arrowBackIcon from '../../../assets/arrow_back.svg';
import tabReviewIcon from '../../../assets/tab_review.svg';
import tabViewFeedbackIcon from '../../../assets/tab_view_feedback.svg';
import tabViewOKRsIcon from '../../../assets/tab_view_OKRs.svg';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';
import { getOkrResultColumnLabel } from '../../../utils/cycleUtils';

// Team Review Detail interface
interface TeamReviewDetail {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reviewStatus: string; // Using dynamic status from API instead of hardcoded values
  employeeEmail: string;
  reviewData?: Record<string, unknown>; // Will contain the actual review data
  submittedDate?: string;
  lastModified?: string;
}

// Feedback interface
interface TeamReviewFeedback {
  employeeFeedbackRating: string;
  appreciativeFeedback: string[];
  constructiveFeedback: string[];
}

type TabType = 'review' | 'feedback' | 'okrs';

// Nomination review interfaces
interface NominationReviewManager {
  manager_id?: number;
  manager_name: string;
  manager_email: string;
}

interface NominationReviewee {
  employee_id: string | number;
  id?: number;
  user_id?: number;
  name: string;
  designation: string;
  department: string;
  email: string;
  manager: NominationReviewManager;
}

interface NominationReviewer {
  email: string;
  emp_id: string;
  name: string;
  reviewer_id: number;
  designation: string;
  department: string;
  manager: NominationReviewManager;
}

interface NominationReviewData {
  review_id: string | null;
  nomination_relationship_id: string | null;
  reviewer?: NominationReviewer;
  reviewee: NominationReviewee;
  cycle_id: string | null;
  relationship_type: string | null;
  status: string | null;
  submitted_at: string | null;
  competency?: {
    solution_focused?: number;
    adapt_to_win?: number;
    courage_to_act?: number;
    act_for_enterprise?: number;
    own_it?: number;
  };
  feedback?: {
    appreciative?: string | string[][];
    constructive?: string | string[][];
  };
}

const TeamReviewDetailPageView: React.FC = () => {
  const { nominationRelationshipId } = useParams<{ 
    nominationRelationshipId: string; 
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const { canReviewNominations, canViewOKR, appraisalCycle } = useRBAC();
  const hasActiveCycle = useActiveCycle();
  const resultColumnLabel = getOkrResultColumnLabel(appraisalCycle?.cycle_type);
  
  // Feedback access is controlled specifically by access.nomination_review.
  const hasFeedbackAccess = canReviewNominations();

  // Check if user has access to view OKRs (based on RBAC and country)
  const hasOKRAccess = canViewOKR();
  const requestedTab = searchParams.get('tab');
  
  // Navigation state passed from the team review list supplies the employee
  // metadata needed by the detail page and the OKR API.
  interface TeamReviewNavigationState {
    nominationRelationshipId?: string;
    employeeId?: string;
    employeeName?: string;
    designation?: string;
    department?: string;
    employeeEmail?: string;
    managerName?: string;
  }

  const getNavigationState = (): TeamReviewNavigationState | null => {
    return (location.state as TeamReviewNavigationState) || null;
  };

  // Get nomination relationship ID from route params or navigation state
  const getRelationshipId = () => {
    if (nominationRelationshipId) {
      return nominationRelationshipId;
    }
    // Check navigation state as fallback
    return getNavigationState()?.nominationRelationshipId;
  };
  const [reviewDetail, setReviewDetail] = useState<TeamReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType | null>(
    requestedTab === 'okrs' && hasOKRAccess
      ? 'okrs'
      : requestedTab === 'feedback' && hasFeedbackAccess
        ? 'feedback'
      : hasFeedbackAccess
        ? 'feedback'
        : hasOKRAccess
          ? 'okrs'
          : null
  );

  const [feedback, setFeedback] = useState<TeamReviewFeedback | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [okrsData, setOkrsData] = useState<TeamReviewOKRsData | null>(null);
  const [okrsLoading, setOkrsLoading] = useState(false);
  const [nominationReviews, setNominationReviews] = useState<NominationReviewData | null>(null);
  const [nominationReviewsLoading, setNominationReviewsLoading] = useState(false);
  const [missingRelationshipId] = useState(false);
  
  // Form state
  const [ratings, setRatings] = useState<Record<string, string>>({});
  const [appreciativeFeedback, setAppreciativeFeedback] = useState('');
  const [constructiveFeedback, setConstructiveFeedback] = useState('');
  const [sendEmailCopy, setSendEmailCopy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // OKR Manager Rating state
  const [okrOverallRating, setOkrOverallRating] = useState<number | ''>('');
  const [okrFeedback, setOkrFeedback] = useState('');
  const [okrSubmittedFeedback, setOkrSubmittedFeedback] = useState('');
  const [okrSubmitting, setOkrSubmitting] = useState(false);
  const [showOkrSubmitDialog, setShowOkrSubmitDialog] = useState(false);
  const [showOkrSuccessDialog, setShowOkrSuccessDialog] = useState(false);
  const [showOkrErrorDialog, setShowOkrErrorDialog] = useState(false);
  const [okrErrorMessage, setOkrErrorMessage] = useState('');
  
  // Form validation states (matching nomination review page)
  
  // Check if form should be disabled (when status is submitted)
  const isFormDisabled = nominationReviews?.status?.toLowerCase() === 'submitted';

  // Debug useEffect to log okrsData changes
  useEffect(() => {
    console.log('🔍 Debug - okrsData state changed:', okrsData);
    console.log('🔍 Debug - okrsData.okrs:', okrsData?.okrs);
    console.log('🔍 Debug - okrsData.okrs.length:', okrsData?.okrs?.length);
  }, [okrsData]);
  
  // Validation function to check if form is complete (exact same as nomination review)
  const isFormComplete = () => {
    // Check if all radio buttons are selected
    const allRatingsSelected = Object.values(ratings).every(rating => rating !== '');
    
    // Check if both feedback fields have valid character count
    const appreciativeFeedbackValid = teamReviewService.isValidCharacterCount(appreciativeFeedback).isValid;
    const constructiveFeedbackValid = teamReviewService.isValidCharacterCount(constructiveFeedback).isValid;
    
    return allRatingsSelected && appreciativeFeedbackValid && constructiveFeedbackValid;
  };

  // Validation function to check if draft can be saved (exact same as nomination review)
  const isDraftSaveable = () => {
    // Check if any radio button is selected (always valid for draft)
    const anyRatingSelected = Object.values(ratings).some(rating => rating !== '');
    
    // Check if any feedback text is entered AND meets minimum character requirement
    const appreciativeFeedbackEntered = appreciativeFeedback.trim().length > 0;
    const constructiveFeedbackEntered = constructiveFeedback.trim().length > 0;
    
    // If text is entered, it must meet validation (10+ characters)
    const appreciativeFeedbackValid = appreciativeFeedbackEntered 
      ? teamReviewService.isValidCharacterCount(appreciativeFeedback).isValid 
      : true; // Empty is fine for draft
      
    const constructiveFeedbackValid = constructiveFeedbackEntered 
      ? teamReviewService.isValidCharacterCount(constructiveFeedback).isValid 
      : true; // Empty is fine for draft
    
    // Draft is saveable if:
    // 1. Any radio is selected, OR
    // 2. Any text is entered AND that text meets validation requirements
    const hasValidData = anyRatingSelected || 
      (appreciativeFeedbackEntered && appreciativeFeedbackValid) || 
      (constructiveFeedbackEntered && constructiveFeedbackValid);
    
    return hasValidData;
  };

  // OKR Manager Rating validation functions
  const isOkrRatingComplete = () => {
    return okrOverallRating !== '' && okrOverallRating > 0 && okrOverallRating <= 4;
  };

  const isOkrFeedbackComplete = () => {
    return teamReviewService.isValidCharacterCount(okrFeedback.trim()).isValid;
  };

  const isOkrFormComplete = () => {
    if (!okrsData?.okrs || okrsData.okrs.length === 0) {
      return false;
    }

    return isOkrRatingComplete() && isOkrFeedbackComplete();
  };

  // Handle OKR submission
  const handleOkrSubmit = () => {
    if (!isOkrRatingComplete()) {
      setOkrErrorMessage('Please select an overall rating before submitting.');
      setShowOkrErrorDialog(true);
      return;
    }

    setShowOkrSubmitDialog(true);
  };

  // Confirm OKR submission
  const handleConfirmOkrSubmit = async () => {
    if (!isOkrFormComplete()) {
      setOkrErrorMessage('Please provide feedback before submitting.');
      setShowOkrErrorDialog(true);
      return;
    }

    setOkrSubmitting(true);
    setShowOkrSubmitDialog(false);
    
    try {
      const employeeId = nominationReviews?.reviewee?.employee_id?.toString() || getNavigationState()?.employeeId;

      if (!employeeId || !user?.email) {
        throw new Error('Missing employee ID or manager email');
      }

      // Submit only the overall rating (no individual OKR ratings)
      await teamReviewService.updateManagerOkrRating(
        employeeId, // empId
        user.email, // managerEmail
        Number(okrOverallRating), // selectedRating (overall rating)
        okrFeedback.trim()
      );

      console.log('✅ Overall OKR rating submitted successfully');
      setOkrSubmittedFeedback(okrFeedback.trim());
      setShowOkrSuccessDialog(true);
    } catch (error: any) {
      console.error('❌ Error submitting overall OKR rating:', error);
      setOkrErrorMessage(error?.response?.data?.message || error?.message || 'Failed to submit OKR rating');
      setShowOkrErrorDialog(true);
    } finally {
      setOkrSubmitting(false);
    }
  };
  
  // Dialog states
  const [showNotYouDialog, setShowNotYouDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch review details
  useEffect(() => {
    const fetchReviewDetail = async () => {
      if (authLoading || !user || !nominationRelationshipId) {
        return;
      }

      setLoading(true);
      
      try {
        console.log('🔄 Fetching team review details for nomination relationship ID:', nominationRelationshipId);
        
        // Create a minimal detail object with the nomination relationship ID,
        // pre-filled with data passed from the team review list (if available)
        const navState = getNavigationState();
        const basicDetail: TeamReviewDetail = {
          id: nominationRelationshipId,
          employeeId: navState?.employeeId ? `#${navState.employeeId}` : '#0000000000',
          employeeName: navState?.employeeName || '',
          designation: navState?.designation || '',
          department: navState?.department || '',
          managerName: navState?.managerName || '',
          reviewStatus: 'pending', // Default to pending until we get actual status from API
          employeeEmail: navState?.employeeEmail || 'loading@company.com',
          submittedDate: undefined,
          lastModified: undefined
        };
        
        console.log('✅ Basic team review detail initialized:', basicDetail);
        setReviewDetail(basicDetail);
      } catch (error) {
        console.error('❌ Error fetching team review details:', error);
        
        // Check for 502 Bad Gateway error - navigate to full error page
        if (error && typeof error === 'object' && 'status_code' in error) {
          const statusCode = (error as { status_code: number }).status_code;
          if (statusCode === 502) {
            navigate('/error', {
              state: {
                errorType: 'bad-gateway',
                previousUrl: window.location.pathname,
                retryUrl: window.location.pathname
              }
            });
            return;
          }
        }
        
        setReviewDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewDetail();
  }, [nominationRelationshipId, user, authLoading, navigate]);

  // Fetch feedback on load only when the permitted feedback tab is active.
  useEffect(() => {
    if (!authLoading && user && activeTab === 'feedback' && hasFeedbackAccess) {
      fetchFeedback();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, activeTab, hasFeedbackAccess]);

  const handleBackClick = () => {
    navigate('/team-review');
  };

  // Function to fetch feedback when View Feedback tab is clicked
  const fetchFeedback = async () => {
    if (!hasFeedbackAccess) {
      return;
    }

    if (!user?.email) {
      console.error('Missing user email for feedback fetch');
      return;
    }

    // Get nomination_relationship_id dynamically from URL params or navigation state
    const relationshipId = getRelationshipId();
    if (!relationshipId) {
      console.error('Missing nomination_relationship_id for feedback fetch');
      setFeedback(null);
      return;
    }

    setFeedbackLoading(true);
    
    try {
      console.log('🔄 Fetching feedback from nomination review feedbacks API for email:', user.email, 'and nomination_relationship_id:', relationshipId);
      const response = await teamReviewService.getNominationReviewFeedbacks(user.email, relationshipId);
      
      if (response.status && response.data) {
        console.log('✅ Feedback data fetched successfully:', response.data);

        // Populate nominationReviews from this response so EmployeeInformation is populated
        // Must be done before any early returns
        if (!nominationReviews && response.data.reviewee) {
          setNominationReviews(response.data);
        }
        
        // Check if feedback is actually empty (empty objects or no meaningful data)
        const hasCompetency = response.data.competency && Object.keys(response.data.competency).length > 0;
        const hasFeedback = response.data.feedback && (
          (response.data.feedback.appreciative && response.data.feedback.appreciative.length > 0) ||
          (response.data.feedback.constructive && response.data.feedback.constructive.length > 0)
        );
        
        // If both competency and feedback are empty, treat as no data
        if (!hasCompetency && !hasFeedback) {
          console.log('⚠️ Empty feedback data (no competency and no feedback text)');
          setFeedback(null);
          setFeedbackLoading(false);
          return;
        }
        
        // Helper function to flatten nested arrays of feedback
        const flattenFeedback = (feedbackArray: any): string[] => {
          if (!feedbackArray) return [];
          
          // If it's an array of arrays, flatten and join each inner array
          if (Array.isArray(feedbackArray) && feedbackArray.length > 0 && Array.isArray(feedbackArray[0])) {
            return feedbackArray.map(item => 
              Array.isArray(item) ? item.join(' ') : String(item)
            ).filter(item => item.trim().length > 0);
          }
          
          // If it's already a simple array of strings
          if (Array.isArray(feedbackArray)) {
            return feedbackArray.filter(item => item && String(item).trim().length > 0);
          }
          
          // If it's a single string
          if (typeof feedbackArray === 'string' && feedbackArray.trim().length > 0) {
            return [feedbackArray];
          }
          
          return [];
        };
        
        const appreciative = flattenFeedback(response.data.feedback?.appreciative);
        const constructive = flattenFeedback(response.data.feedback?.constructive);
        
        // If no actual feedback text, show empty state
        if (appreciative.length === 0 && constructive.length === 0) {
          console.log('⚠️ No feedback text found');
          setFeedback(null);
          setFeedbackLoading(false);
          return;
        }
        
        // Transform the nomination review data into feedback format
        const feedbackData: TeamReviewFeedback = {
          employeeFeedbackRating: "4.5", // You can calculate this from competency ratings if needed
          appreciativeFeedback: appreciative.length > 0 ? appreciative : ["No feedback available"],
          constructiveFeedback: constructive.length > 0 ? constructive : ["No feedback available"]
        };
        
        console.log('🔍 Processed feedback data:', feedbackData);
        setFeedback(feedbackData);
      } else {
        console.warn('⚠️ Feedback fetch failed:', response.error);
        setFeedback(null);
      }
    } catch (error) {
      console.error('❌ Error fetching feedback:', error);
      setFeedback(null);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Function to fetch OKRs when View OKRs tab is clicked
  const fetchOKRs = async () => {
    console.log('🚀 ======= STARTING OKRS FETCH =======');



    console.log('🔍 Step 1 - User email check:', user?.email);
    
    if (!user?.email) {
      console.error('❌ Missing user email for OKRs fetch');
      return;
    }

    // Get the employee ID from feedback data when available, otherwise use
    // the value passed from the team review list.
    const empId = nominationReviews?.reviewee?.employee_id?.toString()
      || searchParams.get('emp_id')
      || getNavigationState()?.employeeId;
    console.log('🔍 Step 2 - Employee ID extraction:');
    console.log('  - nominationReviews exists:', !!nominationReviews);
    console.log('  - reviewee exists:', !!nominationReviews?.reviewee);
    console.log('  - employee_id value:', nominationReviews?.reviewee?.employee_id);
    console.log('  - empId (toString):', empId);
    console.log('🔍 Step 3 - Full reviewee object:', nominationReviews?.reviewee);
    console.log('🔍 Step 4 - Available IDs in reviewee:', {
      employee_id: nominationReviews?.reviewee?.employee_id,
      id: nominationReviews?.reviewee?.id,
      user_id: nominationReviews?.reviewee?.user_id,
      email: nominationReviews?.reviewee?.email
    });
    
    console.log('🔍 Step 5 - Employee ID validation:', {
      empIdExists: !!empId,
      empIdValue: empId,
      empIdType: typeof empId
    });
    
    if (!empId) {
      console.error('❌ Missing employee ID for OKRs fetch. Make sure to visit Review tab first.');
      console.log('🔍 Debug - Full nomination reviews data:', JSON.stringify(nominationReviews, null, 2));
      // Show an error message to the user
      setOkrsData(null);
      return;
    }

    console.log('🔍 Step 6 - Setting loading state to true');
    setOkrsLoading(true);
    
    try {




      console.log('� Step 7 - Making API call');
      console.log('  - Email parameter:', user.email);
      console.log('  - Employee ID parameter:', empId);
      console.log('  - Full API call parameters:', { email: user.email, emp_id: empId });
      
      console.log('🌐 Calling teamReviewService.getSelfAppraisalOKRs...');
      const response = await teamReviewService.getManagerViewOKRs(user.email, empId);
      console.log('✅ API call completed, got response:', response);
      
      console.log('🔍 Step 8 - Processing API response:');
      console.log('  - Response exists:', !!response);
      console.log('  - Response.status:', response.status);
      console.log('  - Response.data exists:', !!response.data);
      console.log('  - Response.data.okrs exists:', !!response.data?.okrs);
      console.log('  - Response.data.okrs is array:', Array.isArray(response.data?.okrs));
      console.log('  - Response.data.okrs.length:', response.data?.okrs?.length);
      console.log('  - Full response object:', JSON.stringify(response, null, 2));
      
      if (response.status && response.data) {
        console.log('🔍 Step 9 - Success path - processing data:');
        console.log('  - Data object:', response.data);
        console.log('  - OKRs array:', response.data.okrs);
        console.log('  - OKRs count:', response.data.okrs?.length || 0);
        
        console.log('🔍 Step 10 - Setting state with okrsData');
        setOkrsData(response.data);
        console.log('  - State set successfully with:', response.data);

        // Populate existing manager rating if it exists
        if (response.data.manager_rating && response.data.manager_rating > 0) {
          console.log('🔍 Step 10.1 - Populated existing manager rating:', response.data.manager_rating);
          setOkrOverallRating(response.data.manager_rating);
        }
        setOkrFeedback(response.data.manager_comments || '');
        setOkrSubmittedFeedback(response.data.manager_comments || '');
      } else {
        console.log('🔍 Step 9 - Fallback path - using response data anyway:');
        console.log('  - Response.status was:', response.status);
        console.log('  - Response.error:', response.error);
        console.log('  - Still setting data:', response.data);
        
        console.log('🔍 Step 10 - Setting fallback state');
        setOkrsData(response.data);
        console.log('  - Fallback state set with:', response.data);

        // Populate existing manager rating in fallback case too
        if (response.data?.manager_rating && response.data.manager_rating > 0) {
          console.log('🔍 Step 10.1 - Populated existing manager rating (fallback):', response.data.manager_rating);
          setOkrOverallRating(response.data.manager_rating);
        }
        setOkrFeedback(response.data?.manager_comments || '');
        setOkrSubmittedFeedback(response.data?.manager_comments || '');
      }
    } catch (error) {
      console.log('🔍 Step 11 - Error occurred:');
      console.error('❌ Error fetching self appraisal OKRs:', error);
      console.log('  - Error type:', typeof error);
      console.log('  - Error message:', (error as Error)?.message);
      console.log('  - Error object:', error);
      
      console.log('🔍 Step 12 - Setting okrsData to null due to error');
      setOkrsData(null);
    } finally {
      console.log('🔍 Step 13 - Setting loading to false');
      setOkrsLoading(false);
      console.log('🏁 ======= OKRS FETCH COMPLETED =======');
    }
  };

  // Function to fetch nomination review details when Review tab is clicked
  const fetchNominationReviews = async () => {
    if (!user?.email) {
      console.error('Missing user email for nomination review details fetch');
      return;
    }

    // Get nomination_relationship_id dynamically from URL params or navigation state
    const relationshipId = getRelationshipId();
    if (!relationshipId) {
      console.error('Missing nomination_relationship_id for nomination review details fetch');
      setNominationReviews(null);
      return;
    }

    setNominationReviewsLoading(true);
    
    try {
      console.log('🔄 Fetching nomination review details for email:', user.email, 'and nomination_relationship_id:', relationshipId);
      const response = await teamReviewService.getNominationReviewDetails(user.email, relationshipId);
      
      if (response.status && response.data) {
        console.log('✅ Nomination review details fetched successfully:', response.data);
        setNominationReviews(response.data);
        
        // Update reviewDetail with actual status from API
        if (reviewDetail && response.data.status) {
          setReviewDetail(prev => prev ? {
            ...prev,
            reviewStatus: response.data.status
          } : prev);
        }
        
        // Populate form fields with existing data if available
        if (response.data.competency) {
          const competencyData = response.data.competency;
          setRatings({
            'rating-1': competencyData.solution_focused?.toString() || '',
            'rating-2': competencyData.adapt_to_win?.toString() || '',
            'rating-3': competencyData.courage_to_act?.toString() || '',
            'rating-4': competencyData.act_for_enterprise?.toString() || '',
            'rating-5': competencyData.own_it?.toString() || ''
          });
        }
        
        if (response.data.feedback) {
          setAppreciativeFeedback(response.data.feedback.appreciative || '');
          setConstructiveFeedback(response.data.feedback.constructive || '');
        }
      } else {
        console.warn('⚠️ Nomination review details fetch failed:', response.error);
        setNominationReviews(null);
      }
    } catch (error) {
      console.error('❌ Error fetching nomination review details:', error);
      setNominationReviews(null);
    } finally {
      setNominationReviewsLoading(false);
    }
  };

  // Load OKRs when they are selected, including when they are the default tab.
  // The team review list supplies the employee ID through navigation state,
  // so the OKR flow does not need to call the nomination_review endpoint.
  useEffect(() => {
    if (authLoading || !user || activeTab !== 'okrs' || !hasOKRAccess) {
      return;
    }

    fetchOKRs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, activeTab, hasOKRAccess]);

  // Handle tab change with API call for feedback and OKRs
  const handleTabChange = (tab: TabType) => {
    if (tab === 'feedback' && !hasFeedbackAccess) {
      return;
    }

    setActiveTab(tab);
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('tab', tab);

      if (tab === 'okrs') {
        const employeeId = nominationReviews?.reviewee?.employee_id?.toString()
          || nextParams.get('emp_id')
          || getNavigationState()?.employeeId;

        if (employeeId) {
          nextParams.set('emp_id', employeeId);
        }
      }

      return nextParams;
    }, { replace: true });
    
    // Fetch nomination review details when Review tab is clicked
    if (tab === 'review' && !nominationReviews) {
      fetchNominationReviews();
    }
    
    // Fetch feedback when View Feedback tab is clicked (always fetch fresh data)
    if (tab === 'feedback') {
      fetchFeedback();
    }
  };

  // Handle rating change
  const handleRatingChange = (questionId: string, value: string) => {
    setRatings(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Handle appreciative feedback change
  const handleAppreciativeFeedbackChange = (value: string) => {
    setAppreciativeFeedback(value);
  };

  // Handle constructive feedback change
  const handleConstructiveFeedbackChange = (value: string) => {
    setConstructiveFeedback(value);
  };

  const handleNotYouSuccess = (newEmail: string) => {
    console.log('Email updated successfully:', newEmail);
    // You can add any additional logic here if needed
  };

  // Handle Save as Draft
  const handleSaveAsDraft = () => {
    setShowSaveDialog(true);
  };

  // Handle Submit
  const handleSubmit = () => {
    setShowSubmitDialog(true);
  };

  // Confirm Save as Draft
  const handleConfirmSave = async () => {
    setSubmitting(true);
    setShowSaveDialog(false);
    
    try {
      // Temporary: Add real token if not present (for testing purposes)
      if (!localStorage.getItem('authToken')) {
        console.log('🔧 No auth token found, adding real token for testing');
        localStorage.setItem('authToken', '2b043a896a9b05124ada475a9b512a745cb8aab3');
      }
      
      // Get nomination_relationship_id dynamically
      const relationshipId = getRelationshipId();
      if (!relationshipId) {
        console.error('Missing nomination_relationship_id for save operation');
        setErrorMessage('Missing nomination relationship ID. Please refresh the page and try again.');
        setShowErrorDialog(true);
        return;
      }
      
      // Prepare the API payload
      const payload = {
        nomination_relationship_id: parseInt(relationshipId),
        email: user?.email || '',
        competency: {
          solution_focused: parseInt(ratings['rating-1']) || 0,
          adapt_to_win: parseInt(ratings['rating-2']) || 0,
          courage_to_act: parseInt(ratings['rating-3']) || 0,
          act_for_enterprise: parseInt(ratings['rating-4']) || 0,
          own_it: parseInt(ratings['rating-5']) || 0
        },
        feedback: {
          constructive: constructiveFeedback,
          appreciative: appreciativeFeedback
        },
        is_email_required: sendEmailCopy ? "true" : "false"
      };
      
      console.log('💾 Saving nomination review feedback as draft:', payload);
      
      const response = await teamReviewService.saveNominationReviewFeedback(payload);
      
      if (response.status) {
        console.log('✅ Nomination review feedback saved successfully:', response.data);
        // Draft saved successfully
        // Uncheck the email checkbox
        setSendEmailCopy(false);
        // Refetch the nomination review details to update the form with latest data
        await fetchNominationReviews();
        // Show success message
        setShowSuccessDialog(true);
      } else {
        console.error('❌ Failed to save nomination review feedback:', response.error);
        setErrorMessage(response.message || 'Failed to save review');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error saving nomination review feedback:', error);
      setErrorMessage('An error occurred while saving the review.');
      setShowErrorDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm Submit
  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setShowSubmitDialog(false);
    
    try {
      // Temporary: Add real token if not present (for testing purposes)
      if (!localStorage.getItem('authToken')) {
        console.log('🔧 No auth token found, adding real token for testing');
        localStorage.setItem('authToken', '2b043a896a9b05124ada475a9b512a745cb8aab3');
      }
      
      // Get nomination_relationship_id dynamically
      const relationshipId = getRelationshipId();
      if (!relationshipId) {
        console.error('Missing nomination_relationship_id for submit operation');
        setErrorMessage('Missing nomination relationship ID. Please refresh the page and try again.');
        setShowErrorDialog(true);
        return;
      }
      
      // Prepare the API payload
      const payload = {
        nomination_relationship_id: parseInt(relationshipId),
        email: user?.email || '',
        competency: {
          solution_focused: parseInt(ratings['rating-1']) || 0,
          adapt_to_win: parseInt(ratings['rating-2']) || 0,
          courage_to_act: parseInt(ratings['rating-3']) || 0,
          act_for_enterprise: parseInt(ratings['rating-4']) || 0,
          own_it: parseInt(ratings['rating-5']) || 0
        },
        feedback: {
          constructive: constructiveFeedback,
          appreciative: appreciativeFeedback
        },
        is_email_required: sendEmailCopy ? "true" : "false"
      };
      
      console.log('📤 Submitting nomination review feedback:', payload);
      
      const response = await teamReviewService.submitNominationReviewFeedback(payload);
      
      if (response.status) {
        console.log('✅ Nomination review feedback submitted successfully:', response.data);
        // Uncheck the email checkbox
        setSendEmailCopy(false);
        // Refetch the nomination review details to update the form with latest data
        await fetchNominationReviews();
        // Show success message
        setShowSuccessDialog(true);
      } else {
        console.error('❌ Failed to submit nomination review feedback:', response.error);
        setErrorMessage(response.message || 'Failed to submit review');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('❌ Error submitting nomination review feedback:', error);
      setErrorMessage('An error occurred while submitting the review.');
      setShowErrorDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading team review details...</p>
        </div>
      </div>
    );
  }

  // Only show error states if loading is complete
  if (!loading && !user) {
    return (
      <ErrorState
        type="not-found"
        title="Authentication Required"
        message="No user data available. Please log in to continue."
        showRetryButton={false}
      />
    );
  }

  if (!loading && !reviewDetail) {
    return (
      <ErrorState
        type="not-found"
        title="Team Review Not Found"
        message="The team review you are looking for could not be found."
        onRetry={handleBackClick}
        showRetryButton={true}
      />
    );
  }

  // Guard clauses to prevent rendering with null data
  if (!user || !reviewDetail) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <img src={arrowBackIcon} alt="Back" className="w-5 h-5" />
          <span className='text-primary-800'>Back to Team List</span>
        </button>
      </div>

      {/* Tabs Navigation */}
            {/* Tabs Navigation */}
      <div className="responsive-tabs flex gap-2">
        {/* 360 Feedback Review Tab - Commented Out */}
        {false && <button
          onClick={() => handleTabChange('review')}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === 'review'
              ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
              : 'text-primary-400 hover:bg-gray-200'
          }`}
          role="tab"
          aria-selected={activeTab === 'review'}
        >
          <img 
            src={tabReviewIcon} 
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className={activeTab === 'review' ? 'text-base' : 'text-base'}>
            360 Feedback Review
          </span>
        </button>}
        <DisabledTabTooltip
          disabled={!hasFeedbackAccess}
          content="You do not have permission to view feedback"
        >
          <button
            onClick={() => hasFeedbackAccess && handleTabChange('feedback')}
            disabled={!hasFeedbackAccess}
            className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
              !hasFeedbackAccess
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                : activeTab === 'feedback'
                  ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
                  : 'text-primary-400 hover:bg-gray-200'
            }`}
            role="tab"
            aria-selected={activeTab === 'feedback'}
          >
            <img
              src={tabViewFeedbackIcon}
              alt=""
              className="w-8 h-8 flex-shrink-0"
            />
            <span className={activeTab === 'feedback' ? 'text-base' : 'text-base'}>
              View Feedback
            </span>
          </button>
        </DisabledTabTooltip>
        <DisabledTabTooltip
          disabled={!hasOKRAccess}
          content="This feature is currently available only for India employees, and will soon open for the rest"
        >
          <button
            onClick={() => hasOKRAccess && handleTabChange('okrs')}
            disabled={!hasOKRAccess}
            className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
              !hasOKRAccess
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                : activeTab === 'okrs'
                  ? 'bg-purple-100 text-purple-800 shadow-md outline outline-1 outline-purple-800'
                  : 'text-primary-400 hover:bg-gray-200'
            }`}
            role="tab"
            aria-selected={activeTab === 'okrs'}
          >
            <img 
              src={tabViewOKRsIcon} 
              alt=""
              className="w-5 h-5 flex-shrink-0"
            />
            <span className={activeTab === 'okrs' ? 'text-base' : 'text-base'}>
              OKRs Review
            </span>
          </button>
        </DisabledTabTooltip>
      </div>

      {/* Tab Content */}
      {/* 360 Feedback Review Tab Content - Commented Out */}
      {false && activeTab === 'review' && (
        <div className="space-y-6">
          {missingRelationshipId ? (
            <Card>
              <div className="p-6 text-center">
                <div className="text-red-500">
                  <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-red-900">Missing Nomination Relationship ID</h3>
                  <p className="mt-1 text-sm text-red-700">
                    The nomination relationship ID is required but was not found in the URL or navigation state.
                  </p>
                  <p className="mt-1 text-sm text-red-600">
                    Please navigate to this page from the team reviews list with the correct parameters.
                  </p>
                  <button
                    onClick={handleBackClick}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Back to Team Reviews
                  </button>
                </div>
              </div>
            </Card>
          ) : nominationReviewsLoading ? (
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading review details...</p>
              </div>
            </Card>
          ) : (
            <>
              {/* Employee Information using reusable component */}
              <Card>
                <div className="">
                  <EmployeeInformation
                    employee={{
                      employeeId: nominationReviews?.reviewee?.employee_id ? `#${nominationReviews?.reviewee?.employee_id}` : reviewDetail?.employeeId,
                      employeeName: nominationReviews?.reviewee?.name || reviewDetail?.employeeName,
                      designation: nominationReviews?.reviewee?.designation || reviewDetail?.designation,
                      department: nominationReviews?.reviewee?.department || reviewDetail?.department,
                      email: nominationReviews?.reviewee?.email || reviewDetail?.employeeEmail,
                      managerName: nominationReviews?.reviewee?.manager?.manager_name || reviewDetail?.managerName
                    }}
                    title="Employee Information"
                  />
                </div>
              </Card>

          {/* Rating Table */}
          <Card>
            <div className="overflow-x-auto">
              <div className="bg-purple-800 text-white p-3 rounded-t-lg">
                <div className="responsive-wide-grid grid gap-4 font-medium" style={{gridTemplateColumns: '80px 200px 1fr 1fr 1fr 1fr'}}>
                  <div className="w-24">S.NO.</div>
                  <div>Description/Rating</div>
                  <div className='text-center'>1-Excel</div>
                  <div className='text-center'>2-Fully Met</div>
                  <div className='text-center'>3-Mostly Met</div>
                  <div className='text-center'>4-Requires Action</div>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-b-lg">
                {[
                  { sno: 1, description: 'Solution Focused', tooltip: 'I prioritise customer needs through value-added solutions.' },
                  { sno: 2, description: 'Adapt to Win', tooltip: 'I learn, unlearn and re-learn to succeed.' },
                  { sno: 3, description: 'Courage to Act', tooltip: 'I speak up, challenge norms, and act with purpose and pace.' },
                  { sno: 4, description: 'Act for Enterprise', tooltip: 'I act on what\'s best for PETRONAS.' },
                  { sno: 5, description: 'Own It', tooltip: 'I am accountable to deliver outcomes that matter.' }
                ].map((item, index) => (
                  <div key={item.sno} className={`responsive-wide-grid grid gap-4 p-3 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`} style={{gridTemplateColumns: '80px 200px 1fr 1fr 1fr 1fr'}}>
                    <div className="font-medium pl-2">{item.sno}.</div>
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <span>{item.description}</span>
                        {item.tooltip && (
                          <Tooltip content={item.tooltip}>
                            <svg 
                              className="w-4 h-4 text-gray-400 hover:text-gray-600" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="1"
                        checked={ratings[`rating-${item.sno}`] === '1'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="2"
                        checked={ratings[`rating-${item.sno}`] === '2'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="3"
                        checked={ratings[`rating-${item.sno}`] === '3'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                    <div className='text-center'>
                      <input
                        type="radio"
                        name={`rating-${item.sno}`}
                        className="w-4 h-4 text-purple-600"
                        value="4"
                        checked={ratings[`rating-${item.sno}`] === '4'}
                        onChange={(e) => handleRatingChange(`rating-${item.sno}`, e.target.value)}
                        disabled={isFormDisabled}
                      />
                    </div>
                  </div>
                ))}
              </div>

             

              {/* Feedback Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add your Appreciative Feedback for the Employee
                </label>
                <textarea
                  rows={4}
                  placeholder="(10-10000 characters)"
                  className={`w-full border border-gray-300 rounded px-3 py-2 ${
                    isFormDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  value={appreciativeFeedback}
                  onChange={(e) => handleAppreciativeFeedbackChange(e.target.value)}
                  disabled={isFormDisabled}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {appreciativeFeedback.length} characters
                  {appreciativeFeedback.length < 10 && (
                    <span className="text-red-500 ml-2">Minimum 10 characters required</span>
                  )}
                </div>
              </div>

              {/* Constructive Feedback Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add your Constructive Feedback for the Employee
                </label>
                <textarea
                  rows={4}
                  placeholder="(10-10000 characters)"
                  className={`w-full border border-gray-300 rounded px-3 py-2 ${
                    isFormDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  value={constructiveFeedback}
                  onChange={(e) => handleConstructiveFeedbackChange(e.target.value)}
                  disabled={isFormDisabled}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {constructiveFeedback.length} characters
                  {constructiveFeedback.length < 10 && (
                    <span className="text-red-500 ml-2">Minimum 10 characters required</span>
                  )}
                </div>
              </div>    





              {/* Submit Section */}
              {!isFormDisabled && (
                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4"
                      checked={sendEmailCopy}
                      onChange={(e) => setSendEmailCopy(e.target.checked)}
                    />
                    <span className="text-sm text-gray-600">
                      Issue me a copy of my response on my email id, {user?.email || 'your.email@company.com'}{' '}
                      <span 
                        className="text-red-500 hover:underline focus:outline-none cursor-pointer"
                        onClick={() => setShowNotYouDialog(true)}
                      >
                         Not you?
                      </span>
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      className={`px-4 py-2 text-sm sm:text-base inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-target min-h-[44px] min-w-[44px] border ${
                        isDraftSaveable() && !submitting
                          ? 'text-purple-600 border-purple-600 hover:bg-purple-50 bg-white focus:ring-purple-500'
                          : 'border-gray-400 bg-white text-gray-600 cursor-not-allowed opacity-50'
                      }`}
                      onClick={handleSaveAsDraft}
                      disabled={submitting || !isDraftSaveable()}
                    >
                      {submitting ? 'Saving...' : 'Save as Draft'}
                    </button>


                    
                    <button 
                      className={`px-4 py-2 text-sm sm:text-base inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-target min-h-[44px] min-w-[44px] ${
                        isFormComplete() && !submitting
                          ? 'bg-purple-800 hover:bg-purple-700 text-white focus:ring-purple-500'
                          : 'bg-purple-800 text-white opacity-50 cursor-not-allowed'
                      }`}
                      onClick={handleSubmit}
                      disabled={submitting || !isFormComplete()}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              )}
                </div>
              </Card>

                 {/* Status Information */}
      {((nominationReviews?.status) || reviewDetail?.reviewStatus) && (
        <Card>
          <div className="">
            <div className={`rounded-lg p-4 ${
              (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                ? 'bg-green-50 border border-green-200' 
                : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                ? 'bg-blue-50 border border-blue-200'
                : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'pending'
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {(nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved' ? (
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${
                    (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                      ? 'text-green-800' 
                      : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                      ? 'text-blue-800'
                      : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'pending'
                      ? 'text-yellow-800'
                      : 'text-gray-800'
                  }`}>
                    Review {((nominationReviews?.status || reviewDetail?.reviewStatus)?.charAt(0).toUpperCase() || '') + ((nominationReviews?.status || reviewDetail?.reviewStatus)?.slice(1) || '')}
                  </h3>
                  <div className={`mt-2 text-sm ${
                    (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                      ? 'text-green-700' 
                      : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                      ? 'text-blue-700'
                      : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'pending'
                      ? 'text-yellow-700'
                      : 'text-gray-700'
                  }`}>
                    <p>
                      {(nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'submitted' 
                        ? 'This team review has been successfully submitted.' 
                        : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'approved'
                        ? 'This team review has been approved.'
                        : (nominationReviews?.status || reviewDetail?.reviewStatus)?.toLowerCase() === 'pending'
                        ? 'This team review is still pending completion.'
                        : `This team review status is: ${(nominationReviews?.status || reviewDetail?.reviewStatus)}.`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
            </>
          )}
        </div>
      )}

      {hasFeedbackAccess && activeTab === 'feedback' && (
        <div className="space-y-6">
          {feedbackLoading ? (
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading feedback...</p>
              </div>
            </Card>
          ) : feedback ? (
            <>
               {/* Employee Information using reusable component */}
              <Card>
                <div className="">
                  <EmployeeInformation
                    employee={{
                      employeeId: nominationReviews?.reviewee?.employee_id ? `#${nominationReviews?.reviewee?.employee_id}` : reviewDetail?.employeeId,
                      employeeName: nominationReviews?.reviewee?.name || reviewDetail?.employeeName,
                      designation: nominationReviews?.reviewee?.designation || reviewDetail?.designation,
                      department: nominationReviews?.reviewee?.department || reviewDetail?.department,
                      email: nominationReviews?.reviewee?.email || reviewDetail?.employeeEmail,
                      managerName: nominationReviews?.reviewee?.manager?.manager_name || reviewDetail?.managerName
                    }}
                    title="Employee Information"
                  />
                </div>
              </Card>
              {/* Employee Feedback Rating */}
              {/* <Card>
                <div className="">
                 
                  <div className="bg-purple-50 border border-purple-200 rounded p-2">
                    <div className="">
                      <div className="text-base  text-purple-800 ">
                      Employee Feedback Rating:
                        {feedback.employeeFeedbackRating}
                      </div>
                      
                    </div>
                  </div>
                </div>
              </Card> */}

              {/* Appreciative Feedback */}
              <Card>
                <div className="p-6 border rounded-lg border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Appreciative Feedback <span className='font-normal'>for the Employee</span></h3>
                  <div className="space-y-4">
                    {feedback.appreciativeFeedback.map((feedbackItem, index) => (
                      <div 
                        key={index}
                        className=" border border-gray-400 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {/* <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div> */}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-700 leading-relaxed">
                              {feedbackItem}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

               {/* Constructive Feedback */}
              <Card>
                <div className="p-6 border rounded-lg border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Constructive Feedback <span className='font-normal'>for the Employee</span></h3>
                  <div className="space-y-4">
                    {feedback.constructiveFeedback.map((feedbackItem, index) => (
                      <div 
                        key={index}
                        className=" border border-gray-400 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {/* <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div> */}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-700 leading-relaxed">
                              {feedbackItem}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

            
            </>
          ) : (
            <>
              {/* Employee Information using reusable component */}
              <Card>
                <div className="">
                  <EmployeeInformation
                    employee={{
                      employeeId: nominationReviews?.reviewee?.employee_id ? `#${nominationReviews?.reviewee?.employee_id}` : reviewDetail?.employeeId,
                      employeeName: nominationReviews?.reviewee?.name || reviewDetail?.employeeName,
                      designation: nominationReviews?.reviewee?.designation || reviewDetail?.designation,
                      department: nominationReviews?.reviewee?.department || reviewDetail?.department,
                      email: nominationReviews?.reviewee?.email || reviewDetail?.employeeEmail,
                      managerName: nominationReviews?.reviewee?.manager?.manager_name || reviewDetail?.managerName
                    }}
                    title="Employee Information"
                  />
                </div>
              </Card>
              
              <Card>
                <div className="p-6">
                  <NoDataState
                    title="No Feedback Submitted"
                    description="The team review feedback has not been submitted for this employee yet."
                  />
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {activeTab === 'okrs' && (
        <div className="space-y-6">
          
             {/* Employee Information using reusable component */}
              <Card>
                <div className="">
                  <EmployeeInformation
                    employee={{
                      employeeId: nominationReviews?.reviewee?.employee_id ? `#${nominationReviews?.reviewee?.employee_id}` : reviewDetail?.employeeId,
                      employeeName: nominationReviews?.reviewee?.name || reviewDetail?.employeeName,
                      designation: nominationReviews?.reviewee?.designation || reviewDetail?.designation,
                      department: nominationReviews?.reviewee?.department || reviewDetail?.department,
                      email: nominationReviews?.reviewee?.email || reviewDetail?.employeeEmail,
                      managerName: nominationReviews?.reviewee?.manager?.manager_name || reviewDetail?.managerName
                    }}
                    title="Employee Information"
                  />
                </div>
              </Card>

          {okrsLoading ? (
            
            <Card>
              <div className="p-6 text-center">
                <LoadingSpinner />
                <p className="mt-2 text-sm text-gray-600">Loading OKRs...</p>
              </div>
            </Card>
            
          ) : okrsData && okrsData.okrs && okrsData.okrs.length > 0 ? (
            <Card>
              <div
                className="max-w-full overflow-x-auto rounded-lg border border-gray-200"
                role="region"
                aria-label="Employee OKRs table"
                tabIndex={0}
              >
                <div className="min-w-[820px]">
                {/* Table Header */}
                <div className="bg-purple-800 text-white">
                  <div className="grid gap-4 p-4 font-medium text-left" style={{gridTemplateColumns: '80px 2fr 1.5fr 1.5fr 120px'}}>
                    <div className="text-sm">S.NO.</div>
                    <div className="text-sm">Objective</div>
                    <div className="text-sm">Target</div>
                    <div className="text-sm">{resultColumnLabel}</div>
                    <div className="text-sm text-center">Ratings</div>
                  </div>
                </div>
                
                {/* Table Body */}
                <div className="bg-white">
                  {okrsData.okrs.map((okr, index) => (
                    <div 
                      key={okr.sno} 
                      className={`grid gap-4 p-4 border-b ${index === okrsData.okrs.length - 1 ? 'border-b-0' : 'border-gray-200'}`}
                      style={{gridTemplateColumns: '80px 2fr 1.5fr 1.5fr 120px'}}
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {okr.sno}.
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed" style={{ whiteSpace: "pre-wrap" }}>
                        {okr.objective}
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed" style={{ whiteSpace: "pre-wrap" }}>
                        {okr.target}
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed" style={{ whiteSpace: "pre-wrap" }}>
                        {okr.endYearResults}
                      </div>
                      <div className="text-center">
                        <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 min-w-[60px]">
                          {(() => {
                            const ratingMap: Record<number, string> = {
                              1: '1',
                              2: '2',
                              3: '3',
                              4: '4',
                            };
                            return ratingMap[Number(okr.rating)] || okr.rating;
                          })()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>

                <div className="mt-4 bg-gray-50 border border-gray-200 p-5 rounded-lg">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Overall Rating</div>
                      <div className="text-xs text-gray-500 mt-1">Superior assessment for this OKR review</div>
                    </div>
                    <select
                      value={okrOverallRating}
                      onChange={(e) => setOkrOverallRating(e.target.value === '' ? '' : Number(e.target.value))}
                      className="w-full lg:w-56 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white shadow-sm"
                    >
                      <option value="">Select Rating</option>
                      <option value={1}>1-Excel</option>
                      <option value={2}>2-Fully Met</option>
                      <option value={3}>3-Mostly Met</option>
                      <option value={4}>4-Requires Action</option>
                    </select>
                  </div>
                  {okrSubmittedFeedback.trim() && (
                    <div className="mt-5 rounded-lg border border-purple-100 bg-white shadow-sm">
                      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Manager Comments</div>
                          <div className="text-xs text-gray-500 mt-0.5">Saved feedback for this OKR rating</div>
                        </div>
                        <span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">
                          Submitted
                        </span>
                      </div>
                      <div className="px-4 py-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {okrSubmittedFeedback}
                      </div>
                    </div>
                  )}
                </div>

              <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Submit Overall Rating</h3>
                    <p className="mt-1 text-sm text-gray-600">A feedback note will be requested before final submission.</p>
                  </div>
                  <div className="flex sm:justify-end">
                    <button 
                      className={`w-full sm:w-auto px-6 py-2 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isOkrRatingComplete() && !okrSubmitting
                          ? 'bg-purple-800 hover:bg-purple-700 text-white focus:ring-purple-500'
                          : 'bg-purple-800 text-white opacity-50 cursor-not-allowed'
                      }`}
                      onClick={handleOkrSubmit}
                      disabled={okrSubmitting || !isOkrRatingComplete()}
                    >
                      {okrSubmitting ? 'Submitting...' : 'Submit Overall Rating'}
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-6">
                <NoDataState
                  title="No OKR Data Found"
                  description="This employee has not submitted any OKR data yet."
                />
              </div>
            </Card>
          )}
        </div>
      )}

   

      {/* Not You Dialog */}
      <NotYouDialog
        isOpen={showNotYouDialog}
        onClose={() => setShowNotYouDialog(false)}
        currentEmail={user?.email || 'your.email@company.com'}
        onSuccess={handleNotYouSuccess}
      />

      {/* Save as Draft Dialog */}
      <Dialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save as Draft"
        type="info"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Save"
        onConfirm={handleConfirmSave}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to save this review as a draft? You can continue editing it later.
            </p>
            <p className="text-gray-600 text-sm">
              Your progress will be saved and you can complete the review at any time.
            </p>
          </div>
        }
      />

      {/* Submit Confirmation Dialog */}
      <Dialog
        isOpen={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        title="Submit Review"
        type="warning"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Submit"
        onConfirm={handleConfirmSubmit}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to submit this team review for <strong>{nominationReviews?.reviewee?.name || reviewDetail?.employeeName || 'this employee'}</strong>?
            </p>
            <p className="text-gray-600 text-sm">
              Once submitted, you will not be able to make any changes to this review.
            </p>
          </div>
        }
      />

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
        }}
        title="Success"
        type="success"
        confirmText="OK"
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Your team review has been successfully saved!
            </p>
            {/* <p className="text-gray-600 text-sm">
              You can continue editing or use the back button to return to the team reviews list.
            </p> */}
          </div>
        }
      />

      {/* Error Dialog */}
      <Dialog
        isOpen={showErrorDialog}
        onClose={() => {
          setShowErrorDialog(false);
          setErrorMessage('');
        }}
        title="Error"
        type="error"
        confirmText="OK"
        onConfirm={() => {
          setShowErrorDialog(false);
          setErrorMessage('');
        }}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              {errorMessage}
            </p>
          </div>
        }
      />

      {/* OKR Submit Confirmation Dialog */}
      <Dialog
        isOpen={showOkrSubmitDialog}
        onClose={() => setShowOkrSubmitDialog(false)}
        title="Submit Overall OKR Rating"
        type="warning"
        showCancel={true}
        cancelText="Cancel"
        confirmText="Submit"
        onConfirm={handleConfirmOkrSubmit}
        customActions={
          <>
            <button
              type="button"
              onClick={() => setShowOkrSubmitDialog(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmOkrSubmit}
              disabled={okrSubmitting || !isOkrFormComplete()}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors ${
                okrSubmitting || !isOkrFormComplete()
                  ? 'bg-purple-800 opacity-50 cursor-not-allowed'
                  : 'bg-purple-800 hover:bg-purple-700'
              }`}
            >
              {okrSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </>
        }
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to submit the overall OKR rating for <strong>{nominationReviews?.reviewee?.name || reviewDetail?.employeeName || 'this employee'}</strong>?
            </p>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className="text-sm font-semibold text-gray-900">
                  Feedback
                </label>
                <span className={`text-xs font-medium ${isOkrFeedbackComplete() ? 'text-green-700' : 'text-gray-500'}`}>
                  {okrFeedback.length}/10000
                </span>
              </div>
              <textarea
                rows={4}
                maxLength={10000}
                placeholder="(10-10000 characters)"
                className={`w-full resize-none border rounded-md px-3 py-2 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  okrFeedback.trim().length > 0 && !isOkrFeedbackComplete()
                    ? 'border-red-400'
                    : 'border-gray-300'
                } ${okrSubmitting ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                value={okrFeedback}
                onChange={(e) => setOkrFeedback(e.target.value)}
                disabled={okrSubmitting}
              />
              <div className="text-xs text-gray-500 mt-2">
                {okrFeedback.trim().length < 10 && (
                  <span className="text-red-600">Minimum 10 characters required</span>
                )}
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              Once submitted, the rating and feedback will be saved to the system.
            </p>
          </div>
        }
      />

      {/* OKR Success Dialog */}
      <Dialog
        isOpen={showOkrSuccessDialog}
        onClose={() => setShowOkrSuccessDialog(false)}
        title="Success"
        type="success"
        confirmText="OK"
        onConfirm={() => setShowOkrSuccessDialog(false)}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              Overall OKR rating has been successfully submitted!
            </p>
            <p className="text-gray-600 text-sm">
              The rating and feedback have been saved to the system.
            </p>
          </div>
        }
      />

      {/* OKR Error Dialog */}
      <Dialog
        isOpen={showOkrErrorDialog}
        onClose={() => {
          setShowOkrErrorDialog(false);
          setOkrErrorMessage('');
        }}
        title="Error"
        type="error"
        confirmText="OK"
        onConfirm={() => {
          setShowOkrErrorDialog(false);
          setOkrErrorMessage('');
        }}
        message={
          <div className="space-y-4">
            <p className="text-gray-700">
              {okrErrorMessage}
            </p>
          </div>
        }
      />
    </div>
  );
};

export default TeamReviewDetailPageView;
